package com.siriusxm.pia.components

/**
 * Centralized z-index management system for consistent layering of UI components.
 * This ensures proper stacking order and prevents z-index conflicts.
 */

/**
 * Z-Index layers for different UI components
 */
enum class ZIndexLayer(val baseZIndex: Int) {
    /** Base application content */
    BASE(0),

    /** Dropdowns, selects, and simple overlays */
    DROPDOWN(1000),

    /** Tooltips */
    TOOLTIP(2000),

    /** Popup menus and button popups */
    POPUP(3000),

    /** Modal dialogs */
    MODAL(4000),

    /** Notifications and alerts */
    NOTIFICATION(5000),

    /** Loading overlays */
    LOADING(6000),

    /** Debug overlays and development tools */
    DEBUG(9000)
}

/**
 * Manages z-index allocation within layers to prevent conflicts
 */
object ZIndexManager {
    private val layerCounters = mutableMapOf<ZIndexLayer, Int>()

    /**
     * Get the next available z-index for the specified layer
     */
    fun allocateZIndex(layer: ZIndexLayer): Int {
        val currentCounter = layerCounters[layer] ?: 0
        layerCounters[layer] = currentCounter + 1
        return layer.baseZIndex + currentCounter
    }

    /**
     * Release a z-index allocation (for cleanup when components are disposed)
     */
    fun releaseZIndex(layer: ZIndexLayer, zIndex: Int) {
        val offset = zIndex - layer.baseZIndex
        if (offset >= 0) {
            // Decrement counter if this was the highest allocated z-index
            val currentCounter = layerCounters[layer] ?: 0
            if (offset + 1 == currentCounter) {
                layerCounters[layer] = maxOf(0, currentCounter - 1)
            }
        }
    }

    /**
     * Get the base z-index for a layer without allocating
     */
    fun getBaseZIndex(layer: ZIndexLayer): Int {
        return layer.baseZIndex
    }

    /**
     * Reset all counters (useful for testing)
     */
    fun reset() {
        layerCounters.clear()
    }
}

/**
 * Extension function to get z-index CSS value for a layer
 */
fun ZIndexLayer.toCssZIndex(): String {
    return ZIndexManager.getBaseZIndex(this).toString()
}

/**
 * Extension function to allocate and get z-index CSS value for a layer
 */
fun ZIndexLayer.allocateCssZIndex(): String {
    return ZIndexManager.allocateZIndex(this).toString()
}